import { auth } from '../config/firebase';
import type { Post, Comment as CommunityComment, PaginatedResponse, Topic, CommunityTag } from '@researcher/shared';
import { apiUrl } from '../config/api';

async function getHeaders(requireAuth = false): Promise<HeadersInit> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  const user = auth.currentUser;
  if (user) {
    const token = await user.getIdToken();
    headers['Authorization'] = `Bearer ${token}`;
  } else if (requireAuth) {
    throw new Error('Authentication required');
  }

  return headers;
}

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, options);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.error || `Request failed with status ${response.status}`);
  }
  return data;
}

// --- Topics ---

export async function fetchTopics(): Promise<Topic[]> {
  const headers = await getHeaders();
  const data = await fetchJSON<{ success: boolean; data: Topic[] }>(
    apiUrl('/community/topics'),
    { headers }
  );
  return data.data;
}

// --- Posts ---

export async function fetchPosts(params: {
  topic?: string;
  tag?: string;
  sort?: string;
  limit?: number;
  cursor?: string;
}): Promise<PaginatedResponse<Post>> {
  const headers = await getHeaders();
  const searchParams = new URLSearchParams();
  if (params.topic) searchParams.set('topic', params.topic);
  if (params.tag) searchParams.set('tag', params.tag);
  if (params.sort) searchParams.set('sort', params.sort);
  if (params.limit) searchParams.set('limit', String(params.limit));
  if (params.cursor) searchParams.set('cursor', params.cursor);

  const data = await fetchJSON<{ success: boolean; data: PaginatedResponse<Post> }>(
    apiUrl(`/community/posts?${searchParams}`),
    { headers }
  );
  return data.data;
}

export async function fetchPost(postId: string): Promise<Post> {
  const headers = await getHeaders();
  const data = await fetchJSON<{ success: boolean; data: Post }>(
    apiUrl(`/community/posts/${postId}`),
    { headers }
  );
  return data.data;
}

export async function createPost(postData: {
  title: string;
  body: string;
  type: string;
  topicSlug: string;
  tags?: string[];
}): Promise<Post> {
  const headers = await getHeaders(true);
  const data = await fetchJSON<{ success: boolean; data: Post }>(
    apiUrl('/community/posts'),
    { method: 'POST', headers, body: JSON.stringify(postData) }
  );
  return data.data;
}

export async function updatePost(postId: string, updates: {
  title?: string;
  body?: string;
  tags?: string[];
}): Promise<void> {
  const headers = await getHeaders(true);
  await fetchJSON(apiUrl(`/community/posts/${postId}`), {
    method: 'PATCH',
    headers,
    body: JSON.stringify(updates),
  });
}

export async function deletePost(postId: string): Promise<void> {
  const headers = await getHeaders(true);
  await fetchJSON(apiUrl(`/community/posts/${postId}`), {
    method: 'DELETE',
    headers,
  });
}

// --- Comments ---

export async function fetchComments(postId: string, cursor?: string): Promise<PaginatedResponse<CommunityComment>> {
  const headers = await getHeaders();
  const searchParams = new URLSearchParams();
  if (cursor) searchParams.set('cursor', cursor);

  const data = await fetchJSON<{ success: boolean; data: PaginatedResponse<CommunityComment> }>(
    apiUrl(`/community/posts/${postId}/comments?${searchParams}`),
    { headers }
  );
  return data.data;
}

export async function createComment(postId: string, commentData: {
  body: string;
  parentCommentId?: string;
}): Promise<CommunityComment> {
  const headers = await getHeaders(true);
  const data = await fetchJSON<{ success: boolean; data: CommunityComment }>(
    apiUrl(`/community/posts/${postId}/comments`),
    { method: 'POST', headers, body: JSON.stringify(commentData) }
  );
  return data.data;
}

export async function deleteComment(postId: string, commentId: string): Promise<void> {
  const headers = await getHeaders(true);
  await fetchJSON(apiUrl(`/community/posts/${postId}/comments/${commentId}`), {
    method: 'DELETE',
    headers,
  });
}

// --- Votes ---

export async function vote(postId: string, value: 1 | -1): Promise<void> {
  const headers = await getHeaders(true);
  await fetchJSON(apiUrl(`/community/posts/${postId}/vote`), {
    method: 'POST',
    headers,
    body: JSON.stringify({ value }),
  });
}

export async function removeVote(postId: string): Promise<void> {
  const headers = await getHeaders(true);
  await fetchJSON(apiUrl(`/community/posts/${postId}/vote`), {
    method: 'DELETE',
    headers,
  });
}

export async function fetchUserVote(postId: string): Promise<{ userId: string; value: 1 | -1 } | null> {
  const headers = await getHeaders(true);
  const data = await fetchJSON<{ success: boolean; data: { userId: string; value: 1 | -1 } | null }>(
    apiUrl(`/community/posts/${postId}/vote`),
    { headers }
  );
  return data.data;
}

// --- Saves / Bookmarks ---

export async function savePost(postId: string): Promise<void> {
  const headers = await getHeaders(true);
  await fetchJSON(apiUrl(`/community/posts/${postId}/save`), {
    method: 'POST',
    headers,
  });
}

export async function unsavePost(postId: string): Promise<void> {
  const headers = await getHeaders(true);
  await fetchJSON(apiUrl(`/community/posts/${postId}/save`), {
    method: 'DELETE',
    headers,
  });
}

export async function checkSaved(postId: string): Promise<boolean> {
  const headers = await getHeaders(true);
  const data = await fetchJSON<{ success: boolean; data: { saved: boolean } }>(
    apiUrl(`/community/posts/${postId}/save`),
    { headers }
  );
  return data.data.saved;
}

// --- Tags ---

export async function searchTags(q: string, topic?: string): Promise<CommunityTag[]> {
  const headers = await getHeaders();
  const searchParams = new URLSearchParams();
  if (q) searchParams.set('q', q);
  if (topic) searchParams.set('topic', topic);
  searchParams.set('limit', '10');

  const data = await fetchJSON<{ success: boolean; data: CommunityTag[] }>(
    apiUrl(`/community/tags?${searchParams}`),
    { headers }
  );
  return data.data;
}

// --- Reports ---

export async function reportPost(postId: string, reason: string, commentId?: string): Promise<void> {
  const headers = await getHeaders(true);
  await fetchJSON(apiUrl(`/community/posts/${postId}/report`), {
    method: 'POST',
    headers,
    body: JSON.stringify({ reason, commentId }),
  });
}

// --- Consent ---

export async function checkConsent(): Promise<{ consented: boolean }> {
  const headers = await getHeaders(true);
  const data = await fetchJSON<{ success: boolean; data: { consented: boolean } }>(
    apiUrl('/consent'),
    { headers }
  );
  return data.data;
}

export async function saveConsent(): Promise<void> {
  const headers = await getHeaders(true);
  await fetchJSON(apiUrl('/consent'), {
    method: 'POST',
    headers,
  });
}

// --- My Posts ---

export async function fetchMyPosts(cursor?: string): Promise<PaginatedResponse<Post>> {
  const headers = await getHeaders(true);
  const searchParams = new URLSearchParams();
  if (cursor) searchParams.set('cursor', cursor);

  const data = await fetchJSON<{ success: boolean; data: PaginatedResponse<Post> }>(
    apiUrl(`/community/my-posts?${searchParams}`),
    { headers }
  );
  return data.data;
}

// --- GDPR ---

export async function exportUserData(): Promise<any> {
  const headers = await getHeaders(true);
  const data = await fetchJSON<{ success: boolean; data: any }>(
    apiUrl('/consent/user-data/export'),
    { headers }
  );
  return data.data;
}

export async function deleteUserData(): Promise<void> {
  const headers = await getHeaders(true);
  await fetchJSON(apiUrl('/consent/user-data'), {
    method: 'DELETE',
    headers,
  });
}
